import { UpdateArcDto } from './update.dto'
import { CreateArcDto } from './create.dto'
import { ArcDto } from './model.dto'
import { HasIndex } from '../base/HasIndex'
import { ArcReviewSites, ArcSite } from './types'
import { bsConstants } from '../../module.constants'
import { endOfDay, newDate } from '../../utils'

export const calculateArcDueDate = (dto: CreateArcDto | UpdateArcDto | ArcDto) => {
  if (dto.neverEndingPrivate) {
    return void 0 // No due date
  } else {
    return endOfDay(dto.dueDate || newDate(0))
  }
}

export const getArcSites = (forceAdd?: boolean, requiredCallback?: (fieldName: string, site: number) => boolean): ArcReviewSites => {
  // Some regexes to setup validation for use on input if required.
  const validationRegexes: { [key: string]: RegExp | undefined } = {
    [bsConstants.ARCS.REVIEWS.FLAGS.AMAZON]: /^[A-Z0-9]{10}$/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.GOOGLE]: /^((http|https):\/\/)?(www\.)?(play\.google)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.GOOD_READS]: /^((http|https):\/\/)?(www\.)?(goodreads)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.KOBO]: /^((http|https):\/\/)?(www\.)?((store\.)?kobo)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.BOOK_BUB]: /^((http|https):\/\/)?(www\.)?(bookbub)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.ITUNES]: /^((http|https):\/\/)?(www\.)?((itunes|books)?\.apple)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.BARNES]: /^((http|https):\/\/)?(www\.)?(barnes|barnesandnoble)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.SMASH_WORDS]: /^((http|https):\/\/)?(www\.)?(smashwords)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.STORY_GRAPH]: /^((http|https):\/\/)?((www|app)\.)?(thestorygraph)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.AUDIO_STORY_GRAPH]: /^((http|https):\/\/)?((www|app)\.)?(thestorygraph)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.AUDIBLE_US]: void 0,
    [bsConstants.ARCS.REVIEWS.FLAGS.AUDIBLE_UK]: void 0,
    [bsConstants.ARCS.REVIEWS.FLAGS.AUDIBLE]: void 0,
    [bsConstants.ARCS.REVIEWS.FLAGS.AUDIO_GOOGLE]: /^((http|https):\/\/)?(www\.)?(play\.google)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.AUDIO_GOODREADS]: /^((http|https):\/\/)?(www\.)?(goodreads)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.AUDIO_KOBO]: /^((http|https):\/\/)?(www\.)?((store\.)?kobo)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.AUDIO_BOOK_BUB]: /^((http|https):\/\/)?(www\.)?(bookbub)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.AUDIO_ITUNES]: /^((http|https):\/\/)?(www\.)?((itunes|books)?\.apple)\.com/gm,
    [bsConstants.ARCS.REVIEWS.FLAGS.AUDIO_BARNES]: /^((http|https):\/\/)?(www\.)?(barnes|barnesandnoble)\.com/gm,
  }

  const result: ArcReviewSites = {
    requiredCount: 0,
    sites: []
  }

  for (const siteKey in bsConstants.ARCS.REVIEWS.FLAGS) {
    const site = (bsConstants.ARCS.REVIEWS.FLAGS as HasIndex)[siteKey]
    // Is this the "none" site; skip.
    if (site === 0 || site === bsConstants.ARCS.REVIEWS.FLAGS.AUDIO_AUTHORS_DIRECT) {
      continue
    }
    // Get the field name (property against the book) so we know which one to update
    const fieldName = (bsConstants.ARCS.REVIEWS.FLAG_BOOK_KEYS as HasIndex)[siteKey]
    // is it required?
    const required = typeof requiredCallback === 'function' ? requiredCallback(fieldName, site) : true

    // Show the required ones or ALL if the author has selected the show all button
    if (required || forceAdd) {
      const validationRegex = validationRegexes[site]
      const rules = []
      if (validationRegex !== void 0) {
        if (site === bsConstants.ARCS.REVIEWS.FLAGS.AMAZON) {
          rules.push(
            (val: string) => ((val && val.match(validationRegex)) || !val) || 'ASINs must be 10 characters in length.'
          )
        } else {
          rules.push(
            (val: string) => ((val && val.match(validationRegex)) || !val) || 'Invalid Link for this site.'
          )
        }
      }

      result.sites.push({
        description: (bsConstants.ARCS.REVIEWS.FLAG_DESCRIPTIONS as HasIndex)[site.toString()],
        fieldName,
        site,
        rules
      })

      if (forceAdd && site === bsConstants.ARCS.REVIEWS.FLAGS.AMAZON) {
        result.sites.push({
          description: 'Amazon (Print)',
          fieldName: 'linkAmazonAsinPrint',
          site: bsConstants.ARCS.REVIEWS.FLAGS.AMAZON,
          rules
        })
      }

      if (required) {
        result.requiredCount++
      }
    }
  }

  result.sites = result.sites.sort((a: any, b: any) => {
    return a.fieldName === b.fieldName
      ? 0
      : a.fieldName < b.fieldName
        ? -1
        : 1
  })

  return result
}

export const getArcSitesFromFlags = (flags: number, excludeFlags?: number): ArcSite[] => {
  const results: ArcSite[] = []
  for (const siteKey in bsConstants.ARCS.REVIEWS.FLAGS) {
    const site = (bsConstants.ARCS.REVIEWS.FLAGS as HasIndex)[siteKey]
    // Is this the "none" site?
    // Are we excluding this site?
    // Skip
    if (site === 0 || (excludeFlags && (excludeFlags & site) === site)) {
      continue
    }

    if ((site & flags) === site) {
      results.push({
        description: (bsConstants.ARCS.REVIEWS.FLAG_DESCRIPTIONS as HasIndex)[site.toString()],
        fieldName: (bsConstants.ARCS.REVIEWS.FLAG_BOOK_KEYS as HasIndex)[siteKey],
        site,
        rules: []
      })
    }
  }
  return results
}

// Amazon issues short URLs from a handful of hosts depending on region/channel.
// `a.co` is the global/US one, regional variants share the `amzn.<tld>` shape,
// and `amzn.to` shows up in marketing emails.
const AMAZON_SHORT_URL_HOSTS = ['a.co', 'amzn.to', 'amzn.eu', 'amzn.asia', 'amzn.com']

export const isAmazonShortUrl = (url: string): boolean => {
  if (!url) {
    return false
  }
  return AMAZON_SHORT_URL_HOSTS.some(host => url.startsWith(`https://${host}/`) || url.startsWith(`http://${host}/`))
}

/**
 * See: https://app.nuclino.com/Booksprout/Booksprout-v2/Reviewing-on-Amazon-6ef5174d-ce9d-493a-964d-3f5999d897e4
 * for rules for amazon
 * @param site
 * @param url
 */
export const isValidReviewLink = (site: number, url: string): boolean => {
  const { ...sites } = bsConstants.ARCS.REVIEWS.FLAGS

  let regex
  switch (site) {
    case sites.AMAZON:
      // short url, which can be used for many things by amazon
      // allow it through, it will be replaced with full url later on
      // and revalidated by regex below
      if (isAmazonShortUrl(url)) {
        return true
      }
      // TODO: AG - Restrict to amazon domain as well.
      regex = new RegExp(/\/((review(s?)\/|customer\-review(s?)\/|product-review(s?)\/)*(R[a-zA-Z0-9]{10,14}))|((u=http)(.*)(review(s?)%2F|customer\-review(s?)%2F|product-review(s?)%2F)*(R[a-zA-Z0-9]{10,14}))/gi)
      break
    case sites.BOOK_BUB:
      regex = new RegExp(/^((http|https):\/\/)?(((www\.)?bookbub\.com\/reviews\/\d+))/gi)
      break
    case sites.GOOD_READS:
      regex = new RegExp(/^((http|https):\/\/)?(((www\.)?goodreads\.com\/review\/show\/\d+))/gi)
      break
    case sites.STORY_GRAPH:
    case sites.AUDIO_STORY_GRAPH:
      regex = new RegExp(/^((http|https):\/\/)?(((www|app)\.)?thestorygraph\.com\/reviews\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi)
      break
    default:
      return true
  }

  return regex.test(url)
}
